import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Trash2, Download, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB"); } catch { return iso; }
};

export default function WasteNotes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/waste-notes")).data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.number || "").toLowerCase().includes(q) ||
      (r.transferor_full_name || "").toLowerCase().includes(q) ||
      (r.transferee_full_name || "").toLowerCase().includes(q) ||
      (r.waste_description || "").toLowerCase().includes(q) ||
      (r.ewc_codes || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const remove = async (id) => {
    if (!window.confirm("Delete this waste transfer note?")) return;
    try { await api.delete(`/waste-notes/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="waste-notes-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Compliance</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Waste Transfer Notes</h1>
          <p className="text-sm text-slate-600 mt-1">UK Section 34 EPA documents — keep for 2 years.</p>
        </div>
        <Button asChild data-testid="add-wtn-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <Link to="/waste-notes/new"><Plus className="w-4 h-4 mr-1.5" /> New waste note</Link>
        </Button>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="wtn-search"
            placeholder="Search by number, producer, carrier, recipient, waste or EWC code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm border-slate-300"
          />
        </div>
      </Card>

      <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Number</th>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-left px-4 py-3 font-semibold">Transferor</th>
              <th className="text-left px-4 py-3 font-semibold">Transferee</th>
              <th className="text-left px-4 py-3 font-semibold">Waste</th>
              <th className="text-left px-4 py-3 font-semibold">EWC</th>
              <th className="text-left px-4 py-3 font-semibold">Quantity</th>
              <th className="text-center px-4 py-3 font-semibold">Hazardous</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-10 text-slate-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-16">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto" />
                  <div className="text-slate-700 font-medium mt-2">No waste transfer notes yet</div>
                  <div className="text-slate-500 text-sm mt-1">Click &quot;New waste note&quot; to add your first.</div>
                </td>
              </tr>
            )}
            {!loading && filtered.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`wtn-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">
                  <Link to={`/waste-notes/${r.id}`} className="hover:underline">{r.number}</Link>
                </td>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate" title={r.transferor_full_name}>{r.transferor_full_name || "—"}</td>
                <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate" title={r.transferee_full_name}>{r.transferee_full_name || "—"}</td>
                <td className="px-4 py-3 text-slate-700 max-w-[260px] truncate" title={r.waste_description}>{r.waste_description || "—"}</td>
                <td className="px-4 py-3 text-slate-500 tabular-nums whitespace-nowrap">{r.ewc_codes || "—"}</td>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap max-w-[160px] truncate" title={r.quantity_description}>
                  {r.quantity_description || "—"}
                </td>
                <td className="px-4 py-3 text-center">
                  {r.hazardous
                    ? <Badge className="rounded-sm bg-red-100 text-red-700 hover:bg-red-100 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Yes</Badge>
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <a
                      data-testid={`wtn-pdf-${r.id}`}
                      href={fileUrl(`/waste-notes/${r.id}/pdf`)}
                      download rel="noopener"
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                    ><Download className="w-3.5 h-3.5" /> PDF</a>
                    <Button
                      variant="ghost" size="icon"
                      data-testid={`wtn-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                    ><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
